/**
 * `POST /api/branches`.
 *
 * Two entry points over one implementation, because the store and the tests want
 * different shapes of the same thing:
 *
 *  - `streamBranches` is an async iterator of wire events. Use it when you want to
 *    dispatch `BRANCH_RECEIVED` per suggestion as it lands.
 *  - `requestBranches` collects the whole set and resolves once, with an optional
 *    `onSuggestion` callback for the same incremental behaviour in a non-iterator style.
 *
 * Both tolerate the server buffering: if the whole set arrives on one read, the iterator
 * simply yields several events in quick succession. Nothing here depends on the timing.
 */

import type { BranchRequest, BranchSuggestion } from '@hc/shared';
import {
  parseDebugPayload,
  postJson,
  readLines,
  type ApiOptions,
  type BranchStreamEvent,
  type LlmDebugPayload,
  type SuggestionOrigin,
} from './types.js';

/**
 * Stream branch suggestions as they are generated.
 *
 * Throws `ApiError` if the request is rejected before the body opens. Once the body has
 * opened, a failure arrives as a final `{ type: 'error' }` event rather than an
 * exception — the headers are long gone by then, and the events already yielded are
 * still good.
 */
export async function* streamBranches(
  request: BranchRequest,
  options?: ApiOptions,
): AsyncGenerator<BranchStreamEvent> {
  const response = await postJson('/api/branches', request, options, 'application/x-ndjson');
  for await (const line of readLines(response)) {
    const event = parseEvent(line);
    if (event) yield event;
  }
}

export interface BranchesResult {
  suggestions: BranchSuggestion[];
  /** Maps straight onto `BranchSet.status`. */
  status: 'ready' | 'partial' | 'fallback' | 'failed';
  /** True when the server answered from its cache. Diagnostics only. */
  cached: boolean;
  /** Present when the stream ended in an error event. */
  error?: string;
  /** The composed prompt, when the request asked to see it. */
  debug?: LlmDebugPayload;
}

export interface RequestBranchesOptions extends ApiOptions {
  /** Called once per suggestion, in arrival order, before the promise resolves. */
  onSuggestion?: (suggestion: BranchSuggestion, origin: SuggestionOrigin) => void;
}

/** Collect a whole branch set, reporting each suggestion as it arrives. */
export async function requestBranches(
  request: BranchRequest,
  options?: RequestBranchesOptions,
): Promise<BranchesResult> {
  const suggestions: BranchSuggestion[] = [];
  let status: BranchesResult['status'] = 'failed';
  let cached = false;
  let error: string | undefined;
  let debug: LlmDebugPayload | undefined;

  for await (const event of streamBranches(request, options)) {
    switch (event.type) {
      case 'suggestion':
        suggestions.push(event.suggestion);
        options?.onSuggestion?.(event.suggestion, event.origin);
        break;
      case 'done':
        status = event.status;
        cached = event.cached;
        if (event.debug) debug = event.debug;
        break;
      case 'error':
        error = event.message;
        break;
    }
  }

  const result: BranchesResult = { suggestions, status, cached };
  if (error !== undefined) result.error = error;
  if (debug) result.debug = debug;
  return result;
}

/**
 * Parse one NDJSON line.
 *
 * A line that is not a recognised event is skipped rather than thrown on. The set that
 * did arrive is worth more than strictness about a line we do not understand.
 */
function parseEvent(line: string): BranchStreamEvent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(line);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const record = parsed as Record<string, unknown>;

  if (record.type === 'suggestion' && record.suggestion && typeof record.suggestion === 'object') {
    const origin = record.origin;
    return {
      type: 'suggestion',
      suggestion: record.suggestion as BranchSuggestion,
      origin: origin === 'fallback' || origin === 'repair' || origin === 'model' ? origin : 'model',
    };
  }

  if (record.type === 'done') {
    const status = record.status;
    // `debug` is attached only when it parses. A server that stops sending it, or sends a
    // shape this build does not know, costs the diagnostics sheet an entry and costs the
    // branch set nothing.
    const debug = parseDebugPayload(record.debug);
    return {
      type: 'done',
      status: status === 'fallback' || status === 'partial' ? status : 'ready',
      cached: record.cached === true,
      ...(debug ? { debug } : {}),
    };
  }

  if (record.type === 'error') {
    return {
      type: 'error',
      message: typeof record.message === 'string' ? record.message : 'branch generation failed',
    };
  }

  return null;
}
